import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Input, Modal, Typography, message } from 'antd';
import { SafetyCertificateOutlined } from '@ant-design/icons';
import { applyConfigDefault, requestConfigOtp } from '../../services/adminApi';

const { Paragraph, Text } = Typography;

// OtpGateModal gates a sensitive admin-config action behind an emailed OTP.
// On open it requests a code (scope + action), lets the user enter it, and on
// confirm verifies + performs the action server-side in one call. The parent is
// notified via onVerified(result) so it can refresh, and via onCancel to close.
//
//   scope:   'formGroups' | 'detailConfig'
//   action:  'reset' | 'save' | 'restore'
export default function OtpGateModal({
  open,
  scope,
  action,
  title,
  description,
  confirmLabel = 'Confirm',
  danger = false,
  onCancel,
  onVerified,
}) {
  const [otp, setOtp] = useState('');
  const [sentTo, setSentTo] = useState('');
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState('');
  const requestedRef = useRef(false);

  async function sendOtp() {
    if (!scope || !action) return;
    setSending(true);
    setError('');
    try {
      const res = await requestConfigOtp(scope, action);
      setSentTo(res?.data?.email || res?.email || 'the registered approver');
      message.success('OTP sent');
    } catch (e) {
      setError(e?.message || 'Could not send the OTP');
    } finally {
      setSending(false);
    }
  }

  // Request a fresh code once each time the modal opens; reset state on close so
  // the next open starts clean.
  useEffect(() => {
    if (open && !requestedRef.current) {
      requestedRef.current = true;
      setOtp('');
      setError('');
      setSentTo('');
      sendOtp();
    }
    if (!open) requestedRef.current = false;
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  async function confirm() {
    if (!otp.trim()) {
      setError('Enter the OTP sent to your email');
      return;
    }
    setVerifying(true);
    setError('');
    try {
      const res = await applyConfigDefault(scope, action, otp.trim());
      message.success(res?.data?.message || res?.message || 'Done');
      onVerified?.(res);
    } catch (e) {
      setError(e?.message || 'Verification failed — check the code and try again');
    } finally {
      setVerifying(false);
    }
  }

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      title={<span><SafetyCertificateOutlined /> {title}</span>}
      destroyOnClose
      maskClosable={!verifying}
      footer={[
        <Button key="cancel" onClick={onCancel} disabled={verifying}>Cancel</Button>,
        <Button key="ok" type="primary" danger={danger} loading={verifying} onClick={confirm}>
          {confirmLabel}
        </Button>,
      ]}
    >
      {description && (
        <Paragraph type="secondary" style={{ fontSize: 13, marginBottom: 8 }}>{description}</Paragraph>
      )}
      <Paragraph type="secondary" style={{ fontSize: 13 }}>
        This is a protected action. A one-time code has been emailed to{' '}
        <Text strong>{sentTo || '…'}</Text>. Enter it below to continue.
      </Paragraph>
      <Input
        size="large"
        inputMode="numeric"
        placeholder="Enter OTP"
        value={otp}
        maxLength={6}
        onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
        onPressEnter={confirm}
        autoFocus
      />
      {error && <Alert type="error" showIcon message={error} style={{ marginTop: 12 }} />}
      <div style={{ marginTop: 12 }}>
        <Button type="link" size="small" loading={sending} onClick={sendOtp} style={{ padding: 0 }}>
          Resend code
        </Button>
      </div>
    </Modal>
  );
}
